/**
 * ProfileSetupPane — the FINAL onboarding step: one compact "Set up your
 * profile" card. Everything profile-shaped lives here so the user finishes
 * onboarding with a real identity:
 *
 *   - banner picker   (react-native-image-picker → /uploads/media, same as
 *                      EditProfileScreen: preview local, upload on save)
 *   - avatar picker   (→ /uploads/avatar, server resizes to 512²)
 *   - username        (prefilled with the handle claimed on step 0; live
 *                      availability probe — same validateUsername /
 *                      checkUsernameAvailable / setUsername contract)
 *   - display name    (PATCH /users/me displayName)
 *   - Instagram + Facebook handles (PATCH /users/me socialLinks)
 *
 * Save semantics (imperative, driven by the wizard's "Get started" CTA via
 * ref.save()):
 *   1. Claim the username first if it changed — a taken handle must block
 *      before we burn image uploads.
 *   2. Upload any pending local images (abort on failure, like EditProfile,
 *      so we never persist a half-updated row).
 *   3. PATCH /users/me with only the fields that actually changed.
 *   4. socialLinks goes in a SEPARATE best-effort PATCH: the API's zod schema
 *      accepts an object (z.record(z.string())) but the Prisma column is
 *      String?, so this write may fail server-side — it must never take the
 *      displayName/avatar/banner save down with it.
 *
 * Everything is optional: save() with nothing changed resolves true instantly,
 * and the wizard's Skip bypasses save() entirely.
 *
 * NO SCROLLING: the pane is height-budgeted for iPhone SE (375×667) — compact
 * banner strip, overlapping avatar, three slim field rows (socials share one
 * row). The `compact` prop shrinks paddings/sizes further on short screens.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from 'react-native';
import CachedImage from '../ui/CachedImage';
import { launchImageLibrary } from 'react-native-image-picker';
import { Ionicons } from '../ui/Icon';
import { colors, spacing, radius, fonts } from '../../constants/theme';
import {
  getMe,
  updateMe,
  uploadAvatar,
  uploadMedia,
  getLastUploadError,
  validateUsername,
  checkUsernameAvailable,
  setUsername as claimUsername,
} from '../../services/api';

export interface ProfileSetupHandle {
  /** Persist everything that changed. Resolves true when safe to finish. */
  save: () => Promise<boolean>;
}

interface Props {
  compact: boolean;
  /** Mirrors the pane's busy state into the wizard CTA spinner. */
  onBusyChange?: (busy: boolean) => void;
}

type UsernameStatus =
  | 'idle' | 'invalid' | 'checking' | 'available' | 'taken' | 'unknown' | 'saving' | 'error';

/** socialLinks arrives as a JSON string (Prisma String?) or an object. */
function parseSocialLinks(raw: unknown): Record<string, string> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return parseSocialLinks(parsed);
    } catch { /* opaque string — keep nothing */ }
  }
  return {};
}

const ProfileSetupPane = forwardRef<ProfileSetupHandle, Props>(function ProfileSetupPane(
  { compact, onBusyChange },
  ref
) {
  // ── Prefill from /auth/me ──
  const [loaded, setLoaded] = useState(false);
  const [currentUsername, setCurrentUsername] = useState('');
  const [origDisplayName, setOrigDisplayName] = useState('');
  const [origLinks, setOrigLinks] = useState<Record<string, string>>({});
  const [avatarSaved, setAvatarSaved] = useState('');
  const [bannerSaved, setBannerSaved] = useState('');

  // ── Editable state ──
  const [username, setUsernameText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [avatarLocal, setAvatarLocal] = useState<string | null>(null);
  const [bannerLocal, setBannerLocal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ── Username availability (same probe contract as the step-0 pane) ──
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const checkSeq = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res: any = await getMe();
        const d: any = res?.data ?? res;
        const u = d?.user ?? d;
        if (!alive || !u) return;
        if (typeof u.username === 'string') {
          setCurrentUsername(u.username);
          setUsernameText(u.username);
        }
        if (typeof u.displayName === 'string') {
          setDisplayName(u.displayName);
          setOrigDisplayName(u.displayName);
        }
        if (typeof u.avatar === 'string') setAvatarSaved(u.avatar);
        if (typeof u.banner === 'string') setBannerSaved(u.banner);
        const links = parseSocialLinks(u.socialLinks);
        setOrigLinks(links);
        if (links.instagram) setInstagram(links.instagram);
        if (links.facebook) setFacebook(links.facebook);
      } catch {
        // Non-fatal — the pane still works from a blank slate.
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onUsernameChange = useCallback((text: string) => {
    const next = text.replace(/\s/g, '');
    setUsernameText(next);
    setUsernameError(null);
    if (next.toLowerCase() === currentUsername.toLowerCase()) {
      setUsernameStatus('idle'); // unchanged — nothing to check or save
      return;
    }
    const fmt = validateUsername(next);
    if (fmt) {
      setUsernameStatus(next.length === 0 ? 'idle' : 'invalid');
      setUsernameError(next.length === 0 ? null : fmt);
      return;
    }
    setUsernameStatus('checking');
    const seq = ++checkSeq.current;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const { available, unknown } = await checkUsernameAvailable(next, currentUsername);
      if (seq !== checkSeq.current) return; // superseded by a newer keystroke
      setUsernameStatus(unknown ? 'unknown' : available ? 'available' : 'taken');
    }, 450);
  }, [currentUsername]);

  // ── Pickers (local preview only; upload happens in save()) ──
  const pick = useCallback(async (kind: 'avatar' | 'banner') => {
    try {
      const result = await launchImageLibrary({
        mediaType: 'photo',
        selectionLimit: 1,
        quality: 0.8,
        includeBase64: false,
      });
      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Error', result.errorMessage || 'Could not open the photo library.');
        return;
      }
      const uri = result.assets?.[0]?.uri;
      if (uri) (kind === 'avatar' ? setAvatarLocal : setBannerLocal)(uri);
    } catch {
      Alert.alert('Error', 'Could not open the photo library.');
    }
  }, []);

  // ── Save (imperative — the wizard CTA drives it) ──
  const save = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    onBusyChange?.(true);
    try {
      // 1. Username (must succeed BEFORE we spend uploads on a doomed save)
      const candidate = username.trim().toLowerCase();
      if (candidate && candidate !== currentUsername.trim().toLowerCase()) {
        const fmt = validateUsername(candidate);
        if (fmt) {
          setUsernameStatus('invalid');
          setUsernameError(fmt);
          return false;
        }
        setUsernameStatus('saving');
        const res = await claimUsername(candidate);
        if (!res.success) {
          const msg = res.error || 'Could not save that username.';
          setUsernameStatus(/taken|use|exist/i.test(msg) ? 'taken' : 'error');
          setUsernameError(msg);
          return false;
        }
        setCurrentUsername(candidate);
        setUsernameText(candidate);
        setUsernameStatus('available');
      }

      // 2. Pending images → upload first; abort if one fails so we never
      //    persist a half-updated profile row (EditProfile contract).
      let avatarUrl = avatarSaved;
      let bannerUrl = bannerSaved;
      if (avatarLocal) {
        const url = await uploadAvatar(avatarLocal);
        if (!url) {
          Alert.alert('Upload failed', getLastUploadError() || 'Could not upload your photo.');
          return false;
        }
        avatarUrl = url;
      }
      if (bannerLocal) {
        const url = await uploadMedia(bannerLocal);
        if (!url) {
          Alert.alert('Upload failed', getLastUploadError() || 'Could not upload your banner.');
          return false;
        }
        bannerUrl = url;
      }

      // 3. Main PATCH — only what changed (camelCase keys; snake_case is
      //    silently dropped by the API whitelist).
      const patch: Record<string, any> = {};
      const dn = displayName.trim();
      if (dn && dn !== origDisplayName) patch.displayName = dn;
      if (avatarLocal && avatarUrl) patch.avatar = avatarUrl;
      if (bannerLocal && bannerUrl) patch.banner = bannerUrl;
      if (Object.keys(patch).length > 0) {
        const res = await updateMe(patch);
        if (!res.success) {
          Alert.alert('Profile', res.error || 'Could not save your profile.');
          return false;
        }
        setOrigDisplayName(dn || origDisplayName);
        setAvatarSaved(avatarUrl);
        setBannerSaved(bannerUrl);
        setAvatarLocal(null);
        setBannerLocal(null);
      }

      // 4. Social handles — SEPARATE best-effort PATCH. The API validates
      //    socialLinks as an object (z.record(z.string())) while the Prisma
      //    column is String?, so this write can fail server-side; it must
      //    never sink the save above. Failure is swallowed by design.
      const ig = instagram.trim().replace(/^@+/, '');
      const fb = facebook.trim().replace(/^@+/, '');
      if (ig !== (origLinks.instagram || '') || fb !== (origLinks.facebook || '')) {
        const links: Record<string, string> = { ...origLinks };
        if (ig) links.instagram = ig; else delete links.instagram;
        if (fb) links.facebook = fb; else delete links.facebook;
        const res = await updateMe({ socialLinks: links });
        if (res.success) setOrigLinks(links);
      }

      return true;
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  }, [
    username, currentUsername, displayName, origDisplayName, instagram, facebook,
    origLinks, avatarLocal, bannerLocal, avatarSaved, bannerSaved, onBusyChange,
  ]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  // ── Sizing ──
  const bannerH = compact ? 64 : 88;
  const avatarS = compact ? 56 : 72;
  const fieldH = compact ? 40 : 46;
  const rowGap = compact ? spacing.sm : spacing.md;

  const avatarDisplay = avatarLocal || avatarSaved;
  const bannerDisplay = bannerLocal || bannerSaved;

  const usernameBad =
    usernameStatus === 'taken' || usernameStatus === 'invalid' || usernameStatus === 'error';

  return (
    <View>
      {/* Banner + overlapping avatar */}
      <TouchableOpacity
        style={[styles.banner, { height: bannerH }]}
        onPress={() => pick('banner')}
        activeOpacity={0.85}
        disabled={busy}
      >
        {bannerDisplay ? (
          <CachedImage source={{ uri: bannerDisplay }} style={StyleSheet.absoluteFill} resizeMode="cover" />
        ) : null}
        <View style={styles.bannerOverlay}>
          <Ionicons name="image-outline" size={compact ? 14 : 16} color="#fff" />
          <Text style={styles.bannerText}>{bannerDisplay ? 'Change banner' : 'Add a banner'}</Text>
        </View>
      </TouchableOpacity>

      <View style={[styles.avatarRow, { marginTop: -(avatarS / 2) }]}>
        <TouchableOpacity onPress={() => pick('avatar')} activeOpacity={0.85} disabled={busy}>
          {avatarDisplay ? (
            <CachedImage
              source={{ uri: avatarDisplay }}
              style={{ width: avatarS, height: avatarS, borderRadius: avatarS / 2, borderWidth: 3, borderColor: colors.bg }}
            />
          ) : (
            <View
              style={[
                styles.avatarEmpty,
                { width: avatarS, height: avatarS, borderRadius: avatarS / 2 },
              ]}
            >
              <Ionicons name="camera-outline" size={compact ? 20 : 24} color={colors.primary} />
            </View>
          )}
          <View style={styles.avatarBadge}>
            <Ionicons name="camera" size={11} color="#fff" />
          </View>
        </TouchableOpacity>
        <Text style={styles.avatarHint} numberOfLines={2}>
          {avatarDisplay ? 'Tap to change your photo' : 'Add a profile photo'}
        </Text>
      </View>

      {/* Username */}
      <View style={{ marginTop: rowGap }}>
        <View
          style={[
            styles.field,
            { height: fieldH },
            usernameBad && styles.fieldError,
            usernameStatus === 'available' && styles.fieldOk,
          ]}
        >
          <Text style={styles.fieldGlyph}>@</Text>
          <TextInput
            style={styles.fieldInput}
            value={username}
            onChangeText={onUsernameChange}
            placeholder={loaded ? 'username' : 'loading…'}
            placeholderTextColor={colors.muted}
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="username"
            maxLength={32}
            editable={!busy}
            returnKeyType="done"
          />
          {usernameStatus === 'checking' || usernameStatus === 'saving' ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : usernameStatus === 'available' ? (
            <Ionicons name="checkmark-circle" size={18} color={colors.success} />
          ) : usernameBad ? (
            <Ionicons name="alert-circle" size={18} color={colors.error} />
          ) : null}
        </View>
        {(usernameError || usernameStatus === 'checking' || usernameStatus === 'taken') && (
          <Text style={[styles.hint, usernameBad && styles.hintError]} numberOfLines={1}>
            {usernameError
              ? usernameError
              : usernameStatus === 'checking' ? 'Checking availability…'
              : 'That username is already taken.'}
          </Text>
        )}
      </View>

      {/* Display name */}
      <View style={[styles.field, { height: fieldH, marginTop: rowGap }]}>
        <Ionicons name="person-outline" size={16} color={colors.muted} />
        <TextInput
          style={styles.fieldInput}
          value={displayName}
          onChangeText={setDisplayName}
          placeholder="Display name"
          placeholderTextColor={colors.muted}
          maxLength={50}
          editable={!busy}
          returnKeyType="done"
        />
      </View>

      {/* Socials — one shared row keeps the pane inside the SE height budget */}
      <View style={[styles.socialRow, { marginTop: rowGap }]}>
        <View style={[styles.field, styles.socialField, { height: fieldH }]}>
          <Ionicons name="logo-instagram" size={16} color="#E1306C" />
          <TextInput
            style={styles.fieldInput}
            value={instagram}
            onChangeText={setInstagram}
            placeholder="Instagram"
            placeholderTextColor={colors.muted}
            autoCapitalize="none"
            autoCorrect={false}
            maxLength={40}
            editable={!busy}
            returnKeyType="done"
          />
        </View>
        <View style={[styles.field, styles.socialField, { height: fieldH }]}>
          <Ionicons name="logo-facebook" size={16} color="#1877F2" />
          <TextInput
            style={styles.fieldInput}
            value={facebook}
            onChangeText={setFacebook}
            placeholder="Facebook"
            placeholderTextColor={colors.muted}
            autoCapitalize="none"
            autoCorrect={false}
            maxLength={60}
            editable={!busy}
            returnKeyType="done"
          />
        </View>
      </View>

      <Text style={styles.footnote} numberOfLines={2}>
        All optional — you can edit any of this later from your profile.
      </Text>
    </View>
  );
});

export default ProfileSetupPane;

const styles = StyleSheet.create({
  banner: {
    borderRadius: radius.lg,
    overflow: 'hidden',
    backgroundColor: 'rgba(108,92,231,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.8)',
  },
  bannerOverlay: {
    ...StyleSheet.absoluteFill, // RN 0.85: absoluteFillObject was removed; absoluteFill IS the plain object
    backgroundColor: 'rgba(26,26,46,0.28)',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  bannerText: { ...fonts.semibold, color: '#fff', fontSize: 12 },
  avatarRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.md,
    paddingLeft: spacing.md,
  },
  avatarEmpty: {
    backgroundColor: 'rgba(108,92,231,0.10)',
    borderWidth: 2,
    borderColor: 'rgba(108,92,231,0.25)',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.bg,
  },
  avatarHint: { ...fonts.regular, color: colors.muted, fontSize: 12, flex: 1, paddingBottom: 4 },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: spacing.md,
    borderRadius: radius.lg,
    borderWidth: 1.5,
    borderColor: 'rgba(108,92,231,0.14)',
    backgroundColor: 'rgba(255,255,255,0.75)',
  },
  fieldOk: { borderColor: colors.success },
  fieldError: { borderColor: colors.error },
  fieldGlyph: { ...fonts.semibold, color: colors.muted, fontSize: 15 },
  fieldInput: { flex: 1, ...fonts.semibold, color: colors.ink, fontSize: 14, paddingVertical: 0 },
  hint: { ...fonts.regular, color: colors.muted, fontSize: 11.5, marginTop: 4, marginLeft: 4 },
  hintError: { color: colors.error },
  socialRow: { flexDirection: 'row', gap: spacing.sm },
  socialField: { flex: 1 },
  footnote: {
    ...fonts.regular,
    color: colors.muted,
    fontSize: 11.5,
    marginTop: spacing.md,
    textAlign: 'center',
  },
});
